import {
  cn,
  Icon,
  IconButton,
  Separator,
  Spinner,
  Text,
} from '@risksmart-app/atomic-ui';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router';

import { ActionMenu } from './parts/ActionMenu';
import type {
  ObjectToolbarProps,
  ToolbarActionGroup,
  ToolbarOverflow,
} from './types';
import {
  ICON_BUTTON_SIZE,
  OVERFLOW_DANGER_LABEL,
  OVERFLOW_DOWNLOAD_LABEL,
} from './types';

/**
 * Flatten the overflow config into ordered, labelled groups. Caller `groups`
 * render first (in order), then the synthesised "Download" group (second-last),
 * then the "Danger zone" group (last). Danger items are forced to the
 * `destructive` variant.
 */
const buildOverflowGroups = ({
  groups = [],
  download,
  danger,
}: ToolbarOverflow): ToolbarActionGroup[] => {
  return [
    ...groups,
    ...(download && download.length > 0
      ? [
          {
            id: 'download',
            label: OVERFLOW_DOWNLOAD_LABEL,
            actions: download,
          },
        ]
      : []),
    ...(danger && danger.length > 0
      ? [
          {
            id: 'danger',
            label: OVERFLOW_DANGER_LABEL,
            actions: danger.map((action) => ({
              ...action,
              variant: 'destructive' as const,
            })),
          },
        ]
      : []),
  ];
};

/**
 * Pure, controlled object-level toolbar. Renders the object header on the left
 * (title + optional page count) and a right-aligned action tray on the right.
 *
 * Tray order (left → right):
 *   primary icon buttons → Save disc (or Approve tick) → Cancel X →
 *   divider → overflow ⋮
 *
 * Save and Approve are mutually exclusive: when `cancel.mode === 'cancel'`
 * (or cancel is absent) the Save disc shows; `cancel.mode === 'approve-cancel'`
 * (TPP > Response) shows the Approve tick and hides Save.
 *
 * The X button only renders when `save` is present (or `approve-cancel` mode)
 * and always calls `navigate(-1)` — no `onCancel` prop needed.
 */
const ObjectToolbarView = ({
  header,
  save,
  cancel,
  actions = {},
}: ObjectToolbarProps) => {
  const navigate = useNavigate();
  const overflow = actions.overflow
    ? buildOverflowGroups(actions.overflow)
    : [];

  const hasOverflow = overflow.some((group) => group.actions.length > 0);

  // Which leading tray controls to show. Save and Approve are mutually exclusive:
  // approve-cancel mode (TPP > Response) shows the Approve tick and hides Save;
  // otherwise the Save disc shows when `save` is present. The Cancel X shows
  // whenever the Save disc shows or in approve-cancel mode.
  const isApproveCancelMode = cancel?.mode === 'approve-cancel';
  const showSaveButton = !isApproveCancelMode && !!save;
  const showApproveButton = isApproveCancelMode;
  const showCancelButton = !!cancel && (isApproveCancelMode || !!save);

  // Assemble the leading action tray (everything to the left of the overflow ⋮),
  // spreading each control in only when it should show.
  const trayButtons: ReactNode[] = [
    ...(actions.primary ?? []).map((action) => (
      <IconButton
        key={action.id}
        className={ICON_BUTTON_SIZE}
        aria-label={action.label}
        disabled={action.disabled}
        onClick={action.onClick}
      >
        {action.iconName ? <Icon name={action.iconName} size={'lg'} /> : null}
      </IconButton>
    )),
    ...(showSaveButton
      ? [
          <IconButton
            key={'save'}
            className={ICON_BUTTON_SIZE}
            aria-label={'Save'}
            disabled={save?.disabled || save?.loading}
            onClick={() => save?.onSave()}
          >
            {save?.loading ? (
              <Spinner size={'md'} />
            ) : (
              <Icon
                name={'save-01'}
                size={'lg'}
                className={cn(
                  save?.dirty && !save?.disabled && 'animate-save-pulse'
                )}
              />
            )}
          </IconButton>,
        ]
      : []),
    ...(showApproveButton
      ? [
          <IconButton
            key={'approve'}
            className={ICON_BUTTON_SIZE}
            aria-label={'Approve'}
            onClick={() => cancel?.onApprove?.()}
          >
            <Icon name={'check-2'} size={'lg'} />
          </IconButton>,
        ]
      : []),
    ...(showCancelButton
      ? [
          <IconButton
            key={'cancel'}
            className={ICON_BUTTON_SIZE}
            aria-label={'Cancel'}
            onClick={() => navigate(-1)}
            disabled={save?.loading}
          >
            <Icon name={'x'} size={'lg'} />
          </IconButton>,
        ]
      : []),
  ];

  // The vertical divider only makes sense between leading tray items and the
  // overflow ⋮ — skip it when either side is absent.
  const hasLeadingTrayItems = trayButtons.length > 0;

  return (
    <div
      className={cn(
        'bg-neutral border-neutral-border-minimal sticky top-0 z-10 flex flex-row items-center justify-between gap-2 border-b p-4'
      )}
    >
      <div className={cn('flex min-w-0 flex-1 items-center gap-2')}>
        <Text preset={'heading-lg'} className={cn('min-w-0 truncate')}>
          {header.title}
        </Text>
        {header.counter ? (
          <Text
            preset={'heading-md'}
            variant={'muted'}
            className={cn('shrink-0 whitespace-nowrap font-light')}
          >
            {header.counter}
          </Text>
        ) : null}
      </div>

      <div className={cn('flex shrink-0 items-center gap-1')}>
        {trayButtons}

        {hasOverflow ? (
          <>
            {hasLeadingTrayItems ? (
              <Separator
                orientation={'vertical'}
                className={'h-8 bg-neutral-border-minimal'}
              />
            ) : null}
            <ActionMenu
              groups={overflow}
              trigger={
                <IconButton
                  key={'overflow'}
                  aria-label={'More actions'}
                  className={cn(ICON_BUTTON_SIZE)}
                >
                  <Icon name={'dots-vertical'} size={'lg'} />
                </IconButton>
              }
            />
          </>
        ) : null}
      </div>
    </div>
  );
};

export { ObjectToolbarView };
